// Парсер ошибок self_qr endpoint-ов.
// Backend отдаёт { detail: string } (FastAPI HTTPException), категоризируем по
// pattern-у строки. Маппинг по матрице handoff §6.

export type SelfQrErrorCode =
  | 'instance_not_found'
  | 'session_not_found'
  | 'chat_not_found'
  | 'session_not_connected'
  | 'session_state_invalid'
  | 'sidecar_unavailable'
  | 'sidecar_error'
  | 'forbidden'
  | 'unauthorized'
  | 'validation'
  | 'reply_invalid_parent'
  | 'phone_invalid_format'
  | 'no_external_id'
  | 'network'
  | 'unknown';

// Структурный detail нового формата (PR #298, handoff §6):
// backend отдаёт { detail: { code: 'category:detail', error?: 'human readable' } }.
export interface SelfQrStructuredDetail {
  code: string;
  error?: string;
}

export interface SelfQrParsedError {
  code: SelfQrErrorCode;
  // Сообщение, готовое к показу пользователю (на русском).
  message: string;
  // Сырой detail от backend (для отладки/логирования).
  rawDetail: string;
  // HTTP статус (если был ответ от сервера)
  status?: number;
}

// Маппинг известных структурных кодов → SelfQrParsedError.
// Коды трактуются как непрозрачные строки-литералы (`reply:invalid_parent` и т.п.),
// сравниваются целиком — split(':') нет.
function classifyStructuredDetail(
  detail: SelfQrStructuredDetail,
  status?: number,
): SelfQrParsedError {
  const code = (detail.code || '').trim();
  // Сохраняем error в rawDetail для диагностики (иначе текст от backend теряется).
  const raw = detail.error ? `${code}: ${detail.error}` : code || JSON.stringify(detail);

  switch (code) {
    case 'reply:invalid_parent':
      return {
        code: 'reply_invalid_parent',
        message:
          'Не удалось ответить: исходное сообщение не найдено или из другого чата',
        rawDetail: raw,
        status,
      };
    case 'phone:invalid_format':
      return {
        code: 'phone_invalid_format',
        message: 'Неверный формат номера',
        rawDetail: raw,
        status,
      };
    default:
      // Неизвестный структурный код — делегируем на проверенную статусную логику
      // (401→unauthorized, 404→…, 409→…), чтобы потребители с проверкой по .code не
      // сломались, если backend пришлёт структурный detail для не-400 статуса.
      return classifySelfQrError(detail.error || detail.code || '', status);
  }
}

// Парсит ответ ошибки. Принимает либо строку detail, либо структурный объект { code, error }.
export function classifySelfQrError(
  detail: string | SelfQrStructuredDetail,
  status?: number,
): SelfQrParsedError {
  // Структурный detail нового формата — ветвимся по code, минуя строковую эвристику.
  if (detail && typeof detail === 'object') {
    return classifyStructuredDetail(detail, status);
  }

  const raw = detail || '';
  const lower = raw.toLowerCase();

  // 401 — токен протух
  if (status === 401) {
    return {
      code: 'unauthorized',
      message: 'Сессия истекла. Войдите заново.',
      rawDetail: raw,
      status,
    };
  }

  // 403 — нет scope
  if (status === 403 || lower.includes('недостаточно прав')) {
    return {
      code: 'forbidden',
      message: 'Недостаточно прав. Свяжитесь с админом.',
      rawDetail: raw,
      status,
    };
  }

  // 404 — три разных ресурса
  if (status === 404 || lower.includes('not found')) {
    if (lower.includes('instance')) {
      return {
        code: 'instance_not_found',
        message: 'Инстанс не найден',
        rawDetail: raw,
        status,
      };
    }
    if (lower.includes('chat')) {
      return {
        code: 'chat_not_found',
        message: 'Чат не найден',
        rawDetail: raw,
        status,
      };
    }
    return {
      code: 'session_not_found',
      message: 'Сессия не найдена. Подключитесь заново.',
      rawDetail: raw,
      status,
    };
  }

  // 409 — конфликт состояний сессии
  if (status === 409) {
    // "session status is '<X>', need 'connected'"
    if (lower.includes("session status") && lower.includes('need')) {
      return {
        code: 'session_not_connected',
        message: 'Сессия отключена — переподключите WhatsApp',
        rawDetail: raw,
        status,
      };
    }
    if (lower.includes('sidecar_session_id')) {
      return {
        code: 'session_state_invalid',
        message: 'Внутренняя ошибка сессии. Сообщите backend-team.',
        rawDetail: raw,
        status,
      };
    }
    return {
      code: 'session_state_invalid',
      message: 'Конфликт состояния сессии',
      rawDetail: raw,
      status,
    };
  }

  // 502 / 503 — sidecar
  if (status === 502 || status === 503 || lower.startsWith('sidecar error')) {
    if (lower.includes('unreachable') || status === 503) {
      return {
        code: 'sidecar_unavailable',
        message: 'Sidecar offline. Свяжитесь с поддержкой.',
        rawDetail: raw,
        status,
      };
    }
    return {
      code: 'sidecar_error',
      message: 'Сервис WhatsApp временно недоступен. Попробуйте через минуту.',
      rawDetail: raw,
      status,
    };
  }

  if (status === 422) {
    // forward/edit/delete/star/pin требуют external_message_id; если его нет — backend
    // отдаёт 422 с упоминанием external_message_id. Сообщение ещё не доставлено.
    // COORDINATION: backend пока отдаёт 422 строкой; при переходе на структурный {code}
    // заменить substring-match на явный код no_external_id.
    if (lower.includes('external_message_id')) {
      return {
        code: 'no_external_id',
        message: 'Сообщение ещё не доставлено — действие недоступно',
        rawDetail: raw,
        status,
      };
    }
    return {
      code: 'validation',
      message: raw || 'Неверные данные запроса',
      rawDetail: raw,
      status,
    };
  }

  return {
    code: 'unknown',
    message: raw || 'Неизвестная ошибка',
    rawDetail: raw,
    status,
  };
}

// Удобный helper: достать `detail` из Response (с graceful fallback) и классифицировать.
export async function parseSelfQrErrorResponse(res: Response): Promise<SelfQrParsedError> {
  let detail: string | SelfQrStructuredDetail = '';
  try {
    const body = await res.json();
    if (typeof body?.detail === 'string') {
      detail = body.detail;
    } else if (
      body?.detail &&
      typeof body.detail === 'object' &&
      typeof body.detail.code === 'string'
    ) {
      // Структурный формат нового бэка (PR #298): { code, error? } — пробрасываем как объект,
      // чтобы classifySelfQrError выставил правильный .code в SelfQrApiError.
      detail = { code: body.detail.code, error: body.detail.error };
    } else if (body?.detail && typeof body.detail === 'object') {
      // Объект без code — graceful fallback: классифицируем как строку (по статусу).
      detail = JSON.stringify(body.detail);
    }
  } catch {
    // body — не JSON, оставляем detail пустым
  }
  return classifySelfQrError(detail, res.status);
}

// Если поймали network-error (fetch reject), возвращаем «network».
export function networkError(message?: string): SelfQrParsedError {
  return {
    code: 'network',
    message: message || 'Нет соединения с сервером',
    rawDetail: message || '',
  };
}

// Custom Error class для throw из service layer — сохраняет код ошибки.
export class SelfQrApiError extends Error {
  readonly code: SelfQrErrorCode;
  readonly status?: number;
  readonly rawDetail: string;

  constructor(parsed: SelfQrParsedError) {
    super(parsed.message);
    this.name = 'SelfQrApiError';
    this.code = parsed.code;
    this.status = parsed.status;
    this.rawDetail = parsed.rawDetail;
  }
}
